import React, { createContext, useContext, useEffect, useState, useCallback, useRef, type Dispatch, type SetStateAction } from "react";
import trpc from "@/shared/utils/trpc";
import { COOKIE_KEYS } from "@/shared/constants/cookies.const";
import Cookies from "js-cookie";
import { Prisma } from "@gt/prisma";
import { useNavigate, useLocation } from "react-router-dom";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import {
  AUTH_SESSION_CHANGED_EVENT,
  invalidateClientSession,
  isInvalidSessionTrpcError,
} from "@/shared/utils/authSession.util";
import { portalServerLogout } from "@/shared/utils/portalRefresh";
import {
  readGuestShortlistCompanyIds,
  readGuestShortlistEcosystemHeadUserIds,
  readGuestShortlistExpertUserIds,
  removeGuestShortlistCompanyIds,
  removeGuestShortlistEcosystemHeadUserIds,
  removeGuestShortlistExpertUserIds,
} from "@/shared/utils/guestShortlistStorage";
import {
  hasClientCreatedProfile,
  isExpoSignupUser,
  isWebsiteProfileJobInProgress,
  shouldDeferCompanyProfile,
} from "@/shared/constants/clientOnboarding.const";
import { openCompleteProfilePrompt } from "@/components/onboarding/CompleteProfilePrompt";
import { pendingRequestsReceivedBaselineKey } from "@/shared/constants/pendingRequestsSpotlight.const";
import { jwtDecode } from "jwt-decode";
import { isClusterPortalRole } from "@/shared/utils/clusterPortalRole.util";
import { useUiStore } from "@/state/stores/uiStore";
import { queryClient } from "@/state/queryClient";
import { queryKeys } from "@/state/queryKeys";
import {
  clearBadgeQueries,
  invalidateCollaborationBalance,
  invalidateCollaborationSidebarCounts,
  invalidatePendingRequestCounts,
  useCollaborationBalanceQuery,
  useCollaborationSidebarCountsQuery,
  usePendingRequestCountsQuery,
} from "@/state/hooks/useBadgesQuery";

/** Avoid new Set() identity churn when membership is unchanged (prevents list refetch storms). */
function replaceIdSet(setter: Dispatch<SetStateAction<Set<string>>>, nextIds: Iterable<string>) {
  const next = Array.from(nextIds);
  setter((prev) => {
    if (prev.size === next.length && next.every((id) => prev.has(id))) return prev;
    return new Set(next);
  });
}

function readPendingRequestsBaseline(userId: string): number | null {
  try {
    const raw = localStorage.getItem(pendingRequestsReceivedBaselineKey(userId));
    if (raw === null) return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function writePendingRequestsBaseline(userId: string, received: number) {
  try {
    localStorage.setItem(pendingRequestsReceivedBaselineKey(userId), String(received));
  } catch {
    /* ignore quota / private mode */
  }
}

type UserWithRelations = Prisma.UserGetPayload<{
  include: {
    company: true;
  };
}> & {
  expoDirectoryGuest?: boolean | null;
  signupExpoSlug?: string | null;
  hasActiveExpoMembership?: boolean;
  signupCluster?: {
    headUserId: string;
    industry: string;
    corridorSignupFreeCollaborationEnabled?: boolean;
    clusterSignupCompanyIntentEnabled?: boolean;
  } | null;
};

interface UserContextType {
  isLoggedIn: boolean;
  hasProfile: boolean | null;
  setHasProfile: React.Dispatch<React.SetStateAction<boolean | null>>;
  userDetails: UserWithRelations | null | undefined;
  setUserDetails: React.Dispatch<React.SetStateAction<UserWithRelations | null | undefined>>;
  /** Resolves to the loaded user (or null). Use for post-login navigation from `company.status`. */
  refetchUser: () => Promise<UserWithRelations | null>;

  shortlistedIds: Set<string>;
  addToShortlist: (companyId: string) => Promise<void>;
  removeFromShortlist: (companyId: string) => Promise<void>;

  shortlistedEcosystemHeadIds: Set<string>;
  addEcosystemHeadToShortlist: (ecosystemHeadUserId: string) => Promise<void>;
  removeEcosystemHeadFromShortlist: (ecosystemHeadUserId: string) => Promise<void>;

  shortlistedExpertIds: Set<string>;
  addExpertToShortlist: (expertUserId: string) => Promise<void>;
  removeExpertFromShortlist: (expertUserId: string) => Promise<void>;

  interestedCompanyIds: Set<string>;
  hasCollaborationWith: (companyId: string) => boolean;
  addInterestedCompany: (companyId: string) => void;
  removeInterestedCompany: (companyId: string) => void;

  /** Pending collaboration requests: received = others requested you; sent = you requested others */
  pendingRequestCounts: { received: number; sent: number };
  upcomingMeetingCount: number;
  refreshPendingInterestCount: () => void;
  newRequestsSpotlightOpen: boolean;
  acknowledgeNewRequestsSpotlight: () => void;

  collaborationSidebarCounts: { active: number; closed: number };
  refreshCollaborationSidebarCounts: () => void;

  collaborationBalance: number | null;
  collaborationBalanceLoadFailed: boolean;
  refreshCollaborationBalance: () => void;

  syncGuestShortlistToServer: () => Promise<void>;

  maintenanceEnabled: boolean | null;
  refetchMaintenance: () => void;
  logout: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUserContext must be used inside UserContextProvider");
  }
  return context;
};

export const UserContextProvider = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [hasProfile, setHasProfile] = useState<boolean | null>(null);
  const [userDetails, setUserDetails] = useState<UserWithRelations | null | undefined>(undefined);
  const [maintenanceEnabled, setMaintenanceEnabled] = useState<boolean | null>(null);
  const [shortlistedIds, setShortlistedIds] = useState<Set<string>>(new Set());
  const [shortlistedEcosystemHeadIds, setShortlistedEcosystemHeadIds] = useState<Set<string>>(new Set());
  const [shortlistedExpertIds, setShortlistedExpertIds] = useState<Set<string>>(new Set());
  const [interestedCompanyIds, setInterestedCompanyIds] = useState<Set<string>>(new Set());
  const [newRequestsSpotlightOpen, setNewRequestsSpotlightOpen] = useState(false);
  const loginBaselineComparedRef = useRef(false);

  const isLoggedIn = !!userDetails;
  const portalRole = (() => {
    const token = Cookies.get(COOKIE_KEYS.token);
    if (!token) return null;
    try {
      return jwtDecode<{ role?: "ADMIN" | "CLIENT" | "FACILITATOR" | "SUB_ADMIN" | "ECOSYSTEM_HEAD" | "ECOSYSTEM_SUBHEAD" | "EXPERT" | "MANAGER" | "EXPO_HEAD" | "EXPO_VISITOR" }>(
        token,
      )?.role ?? null;
    } catch {
      return null;
    }
  })();

  const canUseShortlist =
    portalRole === "CLIENT" ||
    portalRole === "MANAGER" ||
    portalRole === "EXPO_VISITOR" ||
    portalRole === "FACILITATOR" ||
    isClusterPortalRole(portalRole) ||
    portalRole === "EXPERT";
  // Managers use the company portal (shortlist, collaborations, credits) like Clients.
  const isClientUser = portalRole === "CLIENT" || portalRole === "MANAGER";
  const badgesEnabled = isClientUser && Boolean(userDetails?.id);

  const pendingRequestsQuery = usePendingRequestCountsQuery(badgesEnabled);
  const collaborationSidebarQuery = useCollaborationSidebarCountsQuery(badgesEnabled);
  const collaborationBalanceQuery = useCollaborationBalanceQuery(badgesEnabled);

  const pendingRequestCounts = {
    received: pendingRequestsQuery.data?.received ?? 0,
    sent: pendingRequestsQuery.data?.sent ?? 0,
  };
  const upcomingMeetingCount = pendingRequestsQuery.data?.upcomingMeetingCount ?? 0;
  const collaborationSidebarCounts = {
    active: collaborationSidebarQuery.data?.active ?? 0,
    closed: collaborationSidebarQuery.data?.closed ?? 0,
  };
  const collaborationBalance = collaborationBalanceQuery.data?.balance ?? null;
  const collaborationBalanceLoadFailed = collaborationBalanceQuery.data?.failed ?? false;

  /* ---------------- USER ---------------- */
  const fetchShortlistedIds = async () => {
    try {
      if (!canUseShortlist) {
        replaceIdSet(setShortlistedIds, []);
        return [];
      }
      const ids = (await trpc.user.getShortlistedIds.query()) as string[];
      replaceIdSet(setShortlistedIds, ids);
      return ids;
    } catch (err) {
      console.error("Error fetching shortlisted IDs:", err);
      return [];
    }
  };

  const fetchShortlistedEcosystemHeadIds = async () => {
    try {
      if (!isClientUser) {
        replaceIdSet(setShortlistedEcosystemHeadIds, []);
        return [];
      }
      const ids = (await trpc.ecosystemHead.getShortlistedEcosystemHeadIds.query()) as string[];
      replaceIdSet(setShortlistedEcosystemHeadIds, ids);
      return ids;
    } catch (err) {
      console.error("Error fetching shortlisted ecosystem head IDs:", err);
      return [];
    }
  };

  const fetchShortlistedExpertIds = async () => {
    try {
      if (!isClientUser) {
        replaceIdSet(setShortlistedExpertIds, []);
        return [];
      }
      const ids = (await trpc.expert.getShortlistedExpertIds.query()) as string[];
      replaceIdSet(setShortlistedExpertIds, ids);
      return ids;
    } catch (err) {
      console.error("Error fetching shortlisted expert IDs:", err);
      return [];
    }
  };

  const syncGuestShortlistToServer = useCallback(async () => {
    const pendingCompanyIds = readGuestShortlistCompanyIds();
    const pendingCorridorIds = isClientUser ? readGuestShortlistEcosystemHeadUserIds() : [];
    const pendingExpertIds = isClientUser ? readGuestShortlistExpertUserIds() : [];

    if (pendingCompanyIds.length === 0 && pendingCorridorIds.length === 0 && pendingExpertIds.length === 0) {
      return;
    }

    const currentCompanyIds = pendingCompanyIds.length > 0 ? await fetchShortlistedIds() : [];
    const currentCorridorIds =
      isClientUser && pendingCorridorIds.length > 0 ? await fetchShortlistedEcosystemHeadIds() : [];
    const currentExpertIds =
      isClientUser && pendingExpertIds.length > 0 ? await fetchShortlistedExpertIds() : [];

    const syncedCompanyIds: string[] = [];
    for (const companyId of pendingCompanyIds.filter((id) => !currentCompanyIds.includes(id))) {
      try {
        await trpc.user.shortlistCompany.mutate({ companyId });
        syncedCompanyIds.push(companyId);
      } catch (err) {
        console.error("Failed to sync guest shortlist company:", companyId, err);
      }
    }
    if (syncedCompanyIds.length > 0) {
      removeGuestShortlistCompanyIds(syncedCompanyIds);
      setShortlistedIds((prev) => new Set([...prev, ...syncedCompanyIds]));
    }
    removeGuestShortlistCompanyIds(pendingCompanyIds.filter((id) => currentCompanyIds.includes(id)));

    if (!isClientUser) return;

    const syncedCorridorIds: string[] = [];
    for (const ecosystemHeadUserId of pendingCorridorIds.filter((id) => !currentCorridorIds.includes(id))) {
      try {
        await trpc.ecosystemHead.shortlistEcosystemHead.mutate({ ecosystemHeadUserId });
        syncedCorridorIds.push(ecosystemHeadUserId);
      } catch (err) {
        console.error("Failed to sync guest shortlist corridor:", ecosystemHeadUserId, err);
      }
    }
    if (syncedCorridorIds.length > 0) {
      removeGuestShortlistEcosystemHeadUserIds(syncedCorridorIds);
      setShortlistedEcosystemHeadIds((prev) => new Set([...prev, ...syncedCorridorIds]));
    }
    removeGuestShortlistEcosystemHeadUserIds(
      pendingCorridorIds.filter((id) => currentCorridorIds.includes(id)),
    );

    const syncedExpertIds: string[] = [];
    for (const expertUserId of pendingExpertIds.filter((id) => !currentExpertIds.includes(id))) {
      try {
        await trpc.expert.shortlistExpert.mutate({ expertUserId });
        syncedExpertIds.push(expertUserId);
      } catch (err) {
        console.error("Failed to sync guest shortlist expert:", expertUserId, err);
      }
    }
    if (syncedExpertIds.length > 0) {
      removeGuestShortlistExpertUserIds(syncedExpertIds);
      setShortlistedExpertIds((prev) => new Set([...prev, ...syncedExpertIds]));
    }
    removeGuestShortlistExpertUserIds(pendingExpertIds.filter((id) => currentExpertIds.includes(id)));
  }, [isClientUser]);

  const evaluateLoginRequestBaseline = useCallback((userId: string, received: number) => {
    if (loginBaselineComparedRef.current) return;
    loginBaselineComparedRef.current = true;

    const baseline = readPendingRequestsBaseline(userId);
    if (baseline === null) {
      writePendingRequestsBaseline(userId, received);
      return;
    }
    if (received > baseline) {
      setNewRequestsSpotlightOpen(true);
      useUiStore.getState().setNewRequestsSpotlightOpen(true);
    }
  }, []);

  // One-shot login spotlight compare when pending counts first land in the shared cache.
  useEffect(() => {
    if (!badgesEnabled || !userDetails?.id || pendingRequestsQuery.data === undefined) return;
    evaluateLoginRequestBaseline(userDetails.id, pendingRequestsQuery.data.received);
  }, [
    badgesEnabled,
    userDetails?.id,
    pendingRequestsQuery.data,
    evaluateLoginRequestBaseline,
  ]);

  const refreshPendingInterestCount = useCallback(() => {
    void invalidatePendingRequestCounts();
  }, []);

  const refreshCollaborationSidebarCounts = useCallback(() => {
    void invalidateCollaborationSidebarCounts();
  }, []);

  const refreshCollaborationBalance = useCallback(() => {
    void invalidateCollaborationBalance();
  }, []);

  const fetchUserDetails = useCallback((): Promise<UserWithRelations | null> => {
    const token = Cookies.get(COOKIE_KEYS.token);

    if (!token) {
      loginBaselineComparedRef.current = false;
      setUserDetails(null);
      setHasProfile(false);
      replaceIdSet(setShortlistedIds, []);
      replaceIdSet(setShortlistedEcosystemHeadIds, []);
      replaceIdSet(setShortlistedExpertIds, []);
      replaceIdSet(setInterestedCompanyIds, []);
      return Promise.resolve(null);
    }

    // Was "no session" (null) → loading (undefined) so ProtectedRoute does not redirect to /login
    // before getProfile finishes. Keeps existing user object during background refetch.
    setUserDetails((prev) => (prev === null ? undefined : prev));

    return trpc.user.getProfile
      .query({})
      .then((res) => {
        if (!res) return null;
        const user = res as UserWithRelations;
        setUserDetails(user);
        setHasProfile(hasClientCreatedProfile(user));
        queryClient.setQueryData(queryKeys.session.profile(), user);

        // Sidebar sync waits until website profile generation finishes. The banner
        // polls job status on its own; Discover keeps its own page requests.
        if (
          isWebsiteProfileJobInProgress(
            user.company as { websiteProfileJob?: { status?: string | null } | null } | null,
          )
        ) {
          return user;
        }

        // Secondary loads must not block login / navigation (sequential awaits made
        // localhost login feel multi-second even with a fast local DB).
        void (async () => {
          await fetchShortlistedIds();
          if (user.role === "CLIENT" || user.role === "MANAGER") {
            await Promise.all([
              fetchShortlistedEcosystemHeadIds(),
              fetchShortlistedExpertIds(),
            ]);
          }
          // Sync guest shortlist after a real company profile (returning login), or for
          // expo visitors who skip create-profile and still need pending company ids flushed.
          if (hasClientCreatedProfile(user) || isExpoSignupUser(user)) {
            await syncGuestShortlistToServer();
          }
          if (user.role === "CLIENT" || user.role === "MANAGER") {
            fetchInterestedCompanyIds();
          }
        })();

        return user;
      })
      .catch((err) => {
        if (isInvalidSessionTrpcError(err)) {
          invalidateClientSession();
          loginBaselineComparedRef.current = false;
          setUserDetails(null);
          setHasProfile(false);
          replaceIdSet(setShortlistedIds, []);
          replaceIdSet(setShortlistedEcosystemHeadIds, []);
          replaceIdSet(setShortlistedExpertIds, []);
          replaceIdSet(setInterestedCompanyIds, []);
          return null;
        }

        handleTrpcError(err);
        setUserDetails((prev) => {
          if (prev) {
            setHasProfile(hasClientCreatedProfile(prev));
            return prev;
          }
          setHasProfile(false);
          replaceIdSet(setShortlistedIds, []);
          replaceIdSet(setShortlistedEcosystemHeadIds, []);
          replaceIdSet(setShortlistedExpertIds, []);
          replaceIdSet(setInterestedCompanyIds, []);
          return null;
        });
        return null;
      });
  }, [syncGuestShortlistToServer]);

  const acknowledgeNewRequestsSpotlight = useCallback(() => {
    const userId = userDetails?.id;
    if (userId) {
      writePendingRequestsBaseline(userId, pendingRequestCounts.received);
    }
    setNewRequestsSpotlightOpen(false);
    useUiStore.getState().acknowledgeNewRequestsSpotlight();
  }, [userDetails?.id, pendingRequestCounts.received]);

  const fetchInterestedCompanyIds = () => {
    if (!isClientUser || !userDetails?.company?.id) {
      replaceIdSet(setInterestedCompanyIds, []);
      return;
    }
    trpc.collaboration.getInterestedCompanyIds
      .query()
      .then((companyIds: string[]) => {
        setInterestedCompanyIds(new Set(companyIds));
      })
      .catch((err) => {
        console.error("Error fetching interested company IDs:", err);
      });
  };

  const hasCollaborationWith = (companyId: string) => {
    return interestedCompanyIds.has(companyId);
  };

  const addInterestedCompany = (companyId: string) => {
    setInterestedCompanyIds((prev) => new Set([...prev, companyId]));
  };

  const removeInterestedCompany = (companyId: string) => {
    setInterestedCompanyIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(companyId);
      return newSet;
    });
  };

  const notifyProfileRequiredForReachout = () => {
    openCompleteProfilePrompt();
  };

  const addToShortlist = async (companyId: string) => {
    if (!canUseShortlist) return;
    if (shouldDeferCompanyProfile(userDetails)) {
      notifyProfileRequiredForReachout();
      return;
    }
    await trpc.user.shortlistCompany.mutate({ companyId });
    setShortlistedIds((prev) => new Set([...prev, companyId]));
  };

  const removeFromShortlist = async (companyId: string) => {
    if (!canUseShortlist) return;
    await trpc.user.removeShortlist.mutate({ companyId });
    setShortlistedIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(companyId);
      return newSet;
    });
  };

  const addEcosystemHeadToShortlist = async (ecosystemHeadUserId: string) => {
    if (!isClientUser) return;
    if (shouldDeferCompanyProfile(userDetails)) {
      notifyProfileRequiredForReachout();
      return;
    }
    await trpc.ecosystemHead.shortlistEcosystemHead.mutate({ ecosystemHeadUserId });
    setShortlistedEcosystemHeadIds((prev) => new Set([...prev, ecosystemHeadUserId]));
  };

  const removeEcosystemHeadFromShortlist = async (ecosystemHeadUserId: string) => {
    if (!isClientUser) return;
    await trpc.ecosystemHead.removeEcosystemHeadShortlist.mutate({ ecosystemHeadUserId });
    setShortlistedEcosystemHeadIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(ecosystemHeadUserId);
      return newSet;
    });
  };

  const addExpertToShortlist = async (expertUserId: string) => {
    if (!isClientUser) return;
    if (shouldDeferCompanyProfile(userDetails)) {
      notifyProfileRequiredForReachout();
      return;
    }
    await trpc.expert.shortlistExpert.mutate({ expertUserId });
    setShortlistedExpertIds((prev) => new Set([...prev, expertUserId]));
  };

  const removeExpertFromShortlist = async (expertUserId: string) => {
    if (!isClientUser) return;
    await trpc.expert.removeExpertShortlist.mutate({ expertUserId });
    setShortlistedExpertIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(expertUserId);
      return newSet;
    });
  };

  const logout = () => {
    void portalServerLogout().finally(() => {
      invalidateClientSession();
      loginBaselineComparedRef.current = false;
      clearBadgeQueries();
      setUserDetails(null);
      setHasProfile(null);
      replaceIdSet(setShortlistedIds, []);
      replaceIdSet(setShortlistedEcosystemHeadIds, []);
      replaceIdSet(setShortlistedExpertIds, []);
      replaceIdSet(setInterestedCompanyIds, []);
      setNewRequestsSpotlightOpen(false);
      navigate("/login");
    });
  };

  /* ---------------- MAINTENANCE ---------------- */
  const fetchMaintenance = () => {
    trpc.maintenance.get
      .query()
      .then((res) => {
        setMaintenanceEnabled(!!res?.webMaintenanceEnabled);
      })
      .catch((err) => {
        console.error("Error fetching maintenance:", err);
        setMaintenanceEnabled(false);
      });
  };

  /* ---------------- REDIRECT LOGIC ---------------- */
  useEffect(() => {
    if (maintenanceEnabled === null) return;

    // If maintenance is ON → force maintenance page
    if (maintenanceEnabled && location.pathname !== "/maintenance") {
      navigate("/maintenance", { replace: true });
    }

    // If maintenance is OFF → prevent staying on maintenance page
    if (!maintenanceEnabled && location.pathname === "/maintenance") {
      navigate("/login", { replace: true });
    }
  }, [maintenanceEnabled, location.pathname, navigate]);

  /* ---------------- INIT ---------------- */
  useEffect(() => {
    fetchMaintenance();
    fetchUserDetails();
  }, []);

  // Token can appear after silent refresh / login while this provider already mounted
  // with no cookie — re-hydrate so role portals do not gate on a stuck null profile.
  useEffect(() => {
    const onAuthSessionChanged = () => {
      void fetchUserDetails();
    };
    window.addEventListener(AUTH_SESSION_CHANGED_EVENT, onAuthSessionChanged);
    return () => window.removeEventListener(AUTH_SESSION_CHANGED_EVENT, onAuthSessionChanged);
  }, [fetchUserDetails]);

  return (
    <UserContext.Provider
      value={{
        isLoggedIn,
        hasProfile,
        setHasProfile,
        userDetails,
        setUserDetails,
        refetchUser: fetchUserDetails,
        shortlistedIds,
        addToShortlist,
        removeFromShortlist,
        shortlistedEcosystemHeadIds,
        addEcosystemHeadToShortlist,
        removeEcosystemHeadFromShortlist,
        shortlistedExpertIds,
        addExpertToShortlist,
        removeExpertFromShortlist,
        interestedCompanyIds,
        hasCollaborationWith,
        addInterestedCompany,
        removeInterestedCompany,
        pendingRequestCounts,
        upcomingMeetingCount,
        refreshPendingInterestCount,
        newRequestsSpotlightOpen,
        acknowledgeNewRequestsSpotlight,
        collaborationSidebarCounts,
        refreshCollaborationSidebarCounts,
        collaborationBalance,
        collaborationBalanceLoadFailed,
        refreshCollaborationBalance,
        syncGuestShortlistToServer,
        maintenanceEnabled,
        refetchMaintenance: fetchMaintenance,
        logout,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const UserProvider = UserContextProvider;
export const useUser = useUserContext;
